import React, { useEffect } from 'react';
import PolicyLayout from '../components/PolicyLayout';

const tocItems = [
  { href: '#intro', label: '1. Introduction' },
  { href: '#collection', label: '2. Information We Collect' },
  { href: '#use', label: '3. How We Use Your Info' },
  { href: '#sharing', label: '4. How We Share Your Info' },
  { href: '#security', label: '5. Data Security' },
  { href: '#retention', label: '6. Data Retention' },
  { href: '#rights', label: '7. Your Rights' },
  { href: '#children', label: "8. Children's Privacy" },
  { href: '#changes', label: '9. Changes to This Policy' },
  { href: '#contact', label: '10. Contact Us' },
];

export default function PrivacyPolicyPage() {
  useEffect(() => { document.title = 'Chattak | Privacy Policy'; }, []);

  return (
    <PolicyLayout
      title="privacy"
      badgeLabel="Legal Document"
      badgeIcon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
      heroTitle='Your <span class="js-text-underline" data-underline="sm">Privacy</span> Matters <br> to Us'
      heroSubtitle="We are committed to protecting your personal data with transparency and care. This policy explains exactly how Chattak collects, uses, and safeguards your information."
      tocItems={tocItems}
    >
      <div className="policy-section" id="intro">
        <span className="section-number">Section 01</span>
        <h2>Introduction</h2>
        <p>Welcome to Chattak Electronic Money Institution ("Chattak EMI," "we," "us," or "our"). We are committed to protecting your privacy and handling your personal data in an open and transparent manner.</p>
        <p>This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application (the "App") and our services. This policy is designed to comply with the requirements of Afghanistan's regulatory bodies, including Da Afghanistan Bank (DAB) and the Financial Transactions and Reports Analysis Center of Afghanistan (FinTRACA).</p>
        <div className="policy-highlight">
          <p>Please read this policy carefully. <strong>If you do not agree with the terms of this privacy policy, please do not access the App.</strong></p>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="collection">
        <span className="section-number">Section 02</span>
        <h2>Information We Collect</h2>
        <p>To provide you with secure and compliant financial services, we need to collect certain personal information. The types of information we may collect include:</p>

        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>a) Personal Identification Information (PII)</h3>
          <ul>
            <li>Full name, date of birth, gender, and contact details (address, email address, phone number).</li>
            <li>Official identification documents, primarily the Afghan national electronic identity card (e-Tazkira), for KYC and Customer Due Diligence (CDD) purposes.</li>
            <li>Biometric information, if required for identity verification and with your explicit consent.</li>
          </ul>
        </div>

        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg></span>b) Financial Information</h3>
          <ul>
            <li>Transaction history, including dates, amounts, currencies, and counterparties.</li>
            <li>Information related to your e-money wallet, such as balance and funding sources.</li>
            <li>Sources of funds, where required by anti-money laundering regulations.</li>
          </ul>
        </div>

        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg></span>c) Device and Usage Information</h3>
          <ul>
            <li>Information about your mobile device, such as IP address, device type, operating system, and unique device identifiers.</li>
            <li>Information about how you use our App, including access times, features used, and pages viewed.</li>
          </ul>
        </div>

        <div className="subsection">
          <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>d) Communications</h3>
          <ul>
            <li>Information you provide when you contact our customer support team or respond to surveys.</li>
          </ul>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="use">
        <span className="section-number">Section 03</span>
        <h2>How We Use Your Information</h2>
        <p>We use the information we collect for the following purposes:</p>

          <div className="subsection">
            <h3><span className="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></span>
            To Provide and Manage Your Account</h3>
            <ul>
              <li>To create and maintain your account, process your transactions, and provide you with our electronic money services.</li>
            </ul>
          </div>

          <div class="subsection">
            <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3h18v18H3zM9 9h6M9 15h6M9 12h6"/></svg></span>For Legal and Regulatory Compliance</h3>
            <ul>
              <li>To comply with our legal obligations under Afghan law, including AML/CFT regulations.</li>
              <li>Verifying your identity (KYC/CDD).</li>
              <li>Monitoring transactions for suspicious activity.</li>
              <li>Reporting to regulatory bodies like FinTRACA (e.g., Suspicious Transaction Reports).</li>
            </ul>
          </div>

          <div class="subsection">
            <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></span>To Ensure Security</h3>
            <ul><li>To protect our systems and your account from fraud, unauthorized access, and other security risks.</li></ul>
          </div>

          <div class="subsection">
            <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg></span>To Communicate With You</h3>
            <ul><li>To send you important notices, transaction confirmations, security alerts, and support messages.</li></ul>
          </div>

          <div class="subsection">
            <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></span>To Improve Our Services</h3>
            <ul><li>To analyze usage trends, understand our users' needs, and improve the functionality and user experience of our App.</li></ul>
          </div>
        </div>
        

      <hr className="section-divider" />

      <div className="policy-section" id="sharing">
        <span className="section-number">Section 04</span>
        <h2>How We Share Your Information</h2>
        <p>We do not sell your personal information. We may share your information only in the following limited circumstances:</p>


        <div class="subsection">
          <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg></span>With Regulatory and Government Authorities</h3>
          <ul><li>We are required to share information with bodies like Da Afghanistan Bank (DAB) and FinTRACA for compliance, reporting, and law enforcement purposes.</li></ul>
        </div>

        <div class="subsection">
          <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg></span>With Third-Party Service Providers</h3>
          <ul><li>We may share your information with trusted vendors who perform services on our behalf, such as data hosting, fraud detection, and IT security. These providers are contractually obligated to protect your data.</li></ul>
        </div>

        <div class="subsection">
          <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>For Legal Reasons</h3>
          <ul><li>We may disclose your information if necessary to respond to a subpoena, court order, or other legal process, or to protect our rights, property, or the safety of others.</li></ul>
        </div>

        <div class="subsection">
          <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg></span>In Case of Business Transfers</h3>
          <ul><li>If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li></ul>
        </div>
      </div>


      <hr className="section-divider" />

      <div className="policy-section" id="security">
        <span className="section-number">Section 05</span>
        <h2>Data Security</h2>
        <p>We take the security of your data very seriously and have implemented robust technical, administrative, and physical security measures to protect it from unauthorized access, use, alteration, or disclosure.</p>
        <div className="content-img-placeholder">
          <img src="/images/data_security.png" alt="Data Security" />
        </div>
        <div className="subsection">
          <h3><span class="sub-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>Security Measures</h3>
          <ul>
            <li><strong>Access Controls:</strong> We enforce the Principle of Least Privilege and Role-Based Access Control.</li>
            <li><strong>Encryption:</strong> We use strong encryption to protect your sensitive data both at rest and in transit.</li>
            <li><strong>Authentication:</strong> We enforce strong password policies and mandate Multi-Factor Authentication (MFA).</li>
            <li><strong>Monitoring:</strong> We continuously monitor our systems for suspicious activity and potential security threats.</li>
            <li><strong>Training:</strong> All our employees receive regular training on data protection and security best practices.</li>
          </ul>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="retention">
        <span className="section-number">Section 06</span>
        <h2>Data Retention</h2>
        <p>We will retain your personal information for as long as your account is active and for a period thereafter as required by law.</p>
        <div className="policy-highlight">
          <p>Under Afghan regulations, we are required to keep customer documents and transaction records for a <strong>minimum of ten years</strong> after the business relationship ends.</p>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="rights">
        <span className="section-number">Section 07</span>
        <h2>Your Rights</h2>
        <p>You have certain rights regarding your personal information, subject to our legal and regulatory obligations.</p>
        <div class="rights-grid">
          <div class="right-card">
            <div class="right-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </div>
            <h4>Right to Access</h4>
            <p>You can request a copy of the personal information we hold about you.</p>
          </div>
          <div class="right-card">
            <div class="right-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </div>
            <h4>Right to Rectification</h4>
            <p>You can request that we correct any inaccurate or incomplete information.</p>
          </div>
          <div class="right-card">
            <div class="right-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
            </div>
            <h4>Right to be Forgotten</h4>
            <p>You can request deletion of your personal information (subject to AML/CFT obligations).</p>
          </div>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="children">
        <span className="section-number">Section 08</span>
        <h2>Children's Privacy</h2>
        <p>Our services are not intended for individuals under the age of 16. We do not knowingly collect personal information from children under 16. If we become aware that we have inadvertently collected such information, we will take steps to delete it promptly.</p>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="changes">
        <span className="section-number">Section 09</span>
        <h2>Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy in the App and updating the "Last Updated" date at the top of this page.</p>
        <p>We encourage you to review this policy periodically to stay informed about how we are protecting your information.</p>
      </div>

      <hr className="section-divider" />

      <div className="policy-section" id="contact">
        <span className="section-number">Section 10</span>
        <h2>Contact Us</h2>
        <p>If you have any questions or concerns about this Privacy Policy or our data practices, please reach out to us:</p>
        <div className="contact-card">
          <div className="contact-card-info">
            <span className="contact-name">Chattak EMI</span>
            <span className="contact-detail">Traffic Square, PD4, Kabul, Afghanistan</span>
          </div>
          <div className="contact-card-info">
            <span className="contact-detail" style={{ opacity: .5, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Email</span>
            <a href="mailto:legal@chattak.com">legal@chattak.com</a>
          </div>
          <div className="contact-card-info">
            <span className="contact-detail" style={{ opacity: .5, fontSize: '.75rem', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Phone</span>
            <a href="tel:+93787282840">+93 78 728 2840</a>
          </div>
        </div>
      </div>
    </PolicyLayout>
  );
}
